import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

import { getApiBase } from '../config/apiBase';

const BASE = getApiBase();
const MODULES = ['inventory', 'production', 'mrp', 'purchasing', 'calendar', 'forecasts', 'financials'];

function adminFetch(path, token, options = {}) {
  return fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
    ...options,
  }).then(r => r.json()).catch(err => ({ success: false, message: err.message || 'Failed to fetch' }));
}

export default function AdminPanel() {
  const { token } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [msg, setMsg] = useState('');

  const loadUsers = useCallback(async () => {
    const json = await adminFetch('/admin/users', token);
    if (json.success) setUsers(json.data);
  }, [token]);

  const loadRoles = useCallback(async () => {
    const json = await adminFetch('/admin/roles', token);
    if (json.success) setRoles(json.data);
  }, [token]);

  const loadAudit = useCallback(async () => {
    const json = await adminFetch('/admin/audit-logs?limit=200', token);
    if (json.success) { setAuditLogs(json.data.logs); setAuditTotal(json.data.total); }
  }, [token]);

  useEffect(() => { loadUsers(); loadRoles(); }, [loadUsers, loadRoles]);
  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    const json = await adminFetch(`/admin/users/${userId}/status`, token, {
      method: 'PATCH', body: JSON.stringify({ status: newStatus }),
    });
    if (json.success) { flash(`User ${newStatus === 'Active' ? 'activated' : 'deactivated'}`); loadUsers(); }
  };

  const tabs = [
    { id: 'users', label: 'User Management' },
    { id: 'audit', label: 'Audit Log' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Administration Panel</h1>
        {tab === 'users' && (
          <button onClick={() => { setShowCreate(true); setEditUser(null); }}
            className="btn-primary text-xs py-1.5 px-3">
            + Create User
          </button>
        )}
      </div>

      {msg && (
        <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">{msg}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-sap-border/60">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors -mb-px
              ${tab === t.id ? 'border-sap-blue text-sap-blue' : 'border-transparent text-sap-textLight hover:text-sap-text'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <div className="card">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-sap-blue/5 text-sap-textLight">
                <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Name</th>
                <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Email</th>
                <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Role</th>
                <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Last Login</th>
                <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Status</th>
                <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-sap-border/30 hover:bg-sap-blue/3">
                  <td className="px-3 py-2 font-medium text-sap-text">{u.name}</td>
                  <td className="px-3 py-2 text-sap-textLight">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold
                      ${u.role_name === 'Super Admin' ? 'bg-purple-100 text-purple-700'
                        : u.role_name === 'Admin' ? 'bg-blue-100 text-blue-700'
                        : u.role_name === 'Manager' ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'}`}>
                      {u.role_name}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-sap-textLight">
                    {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold
                      ${u.status === 'Active' ? 'bg-emerald-100 text-emerald-700'
                        : u.status === 'Locked' ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-500'}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditUser(u); setShowCreate(true); }}
                        className="p-1 rounded hover:bg-sap-blue/10 text-sap-blue" title="Edit">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button onClick={() => toggleStatus(u.user_id, u.status)}
                        className={`p-1 rounded text-xs font-medium ${u.status === 'Active' ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                        title={u.status === 'Active' ? 'Deactivate' : 'Activate'}>
                        {u.status === 'Active' ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Tab */}
      {tab === 'audit' && (
        <div className="card">
          <div className="px-3 py-2 border-b border-sap-border/60 bg-sap-blue/5 flex justify-between">
            <span className="text-xs font-bold text-sap-text">Audit Log</span>
            <span className="text-2xs text-sap-textLight">{auditTotal} entries</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="bg-sap-blue/5 text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Timestamp</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">User</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Action</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Module</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(log => (
                  <tr key={log.log_id} className="border-t border-sap-border/30">
                    <td className="px-3 py-1.5 text-sap-textLight whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-sap-text">{log.user_email || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-semibold
                        ${log.action.includes('FAIL') || log.action.includes('BLOCK') ? 'bg-red-100 text-red-700'
                        : log.action.includes('SUCCESS') || log.action.includes('CREATED') ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-blue-100 text-blue-700'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-sap-textLight">{log.module}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{log.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit User Modal */}
      {showCreate && (
        <UserModal
          roles={roles}
          user={editUser}
          token={token}
          onClose={() => { setShowCreate(false); setEditUser(null); }}
          onSaved={() => { setShowCreate(false); setEditUser(null); loadUsers(); flash(editUser ? 'User updated' : 'User created'); }}
        />
      )}
    </div>
  );
}

/* ── Create / Edit User Modal ── */
function UserModal({ roles, user, token, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const initialRoleId = user?.role_id || (roles[0]?.role_id || '');
    const selectedRole = roles.find(r => r.role_id === parseInt(initialRoleId));
    const isSuperAdmin = selectedRole?.role_name === 'Super Admin';
    const permissions = isSuperAdmin
      ? MODULES.reduce((acc, m) => ({ ...acc, [m]: false }), {})
      : (user?.permissions || {});
    return {
      name: user?.name || '',
      email: user?.email || '',
      password: '',
      role_id: initialRoleId,
      permissions,
    };
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    if (!user && !form.password) { setError('Password is required'); return; }
    if (form.password && form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password && !/[A-Z]/.test(form.password)) { setError('Password must contain at least 1 uppercase letter'); return; }
    if (form.password && !/[0-9]/.test(form.password)) { setError('Password must contain at least 1 number'); return; }

    setSaving(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        role_id: parseInt(form.role_id),
        module_permissions: form.permissions,
      };
      if (form.password) body.password = form.password;

      const url = user ? `/admin/users/${user.user_id}` : '/admin/users';
      const method = user ? 'PUT' : 'POST';

      const json = await adminFetch(url, token, { method, body: JSON.stringify(body) });
      if (!json.success) { setError(json.message); return; }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = roles.find(r => r.role_id === parseInt(form.role_id));
  const isSuperAdmin = selectedRole?.role_name === 'Super Admin';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-sap-border/60 flex justify-between items-center">
          <span className="text-sm font-bold text-sap-text">{user ? 'Edit User' : 'Create New User'}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{error}</div>}

          <Field label="Full Name" value={form.name} onChange={v => set('name', v)} />
          <Field label="Email" type="email" value={form.email} onChange={v => set('email', v)} />
          <Field label={user ? 'New Password (leave blank to keep)' : 'Password'} type="password" value={form.password} onChange={v => set('password', v)} />

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
            <select value={form.role_id} onChange={e => {
              const val = e.target.value;
              const nextRole = roles.find(r => r.role_id === parseInt(val));
              if (nextRole?.role_name === 'Super Admin') {
                const cleared = MODULES.reduce((acc, m) => ({ ...acc, [m]: false }), {});
                setForm(prev => ({ ...prev, role_id: val, permissions: cleared }));
              } else {
                setForm(prev => ({ ...prev, role_id: val }));
              }
            }}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-sap-blue/30">
              {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.role_name} — {r.description}</option>)}
            </select>
          </div>

          {/* Module Toggles */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Module Access</label>
            <div className="grid grid-cols-2 gap-2">
              {MODULES.map(mod => {
                const isChecked = isSuperAdmin ? false : (form.permissions[mod] !== false);
                return (
                  <label key={mod} className={`flex items-center gap-2 text-xs text-gray-600 ${isSuperAdmin ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input type="checkbox" checked={isChecked} disabled={isSuperAdmin}
                      onChange={e => set('permissions', { ...form.permissions, [mod]: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-sap-blue focus:ring-sap-blue disabled:opacity-50" />
                    <span className="capitalize">{mod}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-1.5 text-xs font-semibold text-white bg-sap-blue rounded-lg hover:bg-sap-blueDark disabled:opacity-60">
              {saving ? 'Saving...' : user ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-sap-blue/30" />
    </div>
  );
}
